import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  fetchCapabilities,
  type Capabilities,
  type Capability,
} from './api/capabilities';

/**
 * The instance's capabilities, fetched once after login and shared.
 *
 * `null` while unknown, which is **not** the same as "all absent": the
 * sidebar renders nothing capability-gated until the answer arrives,
 * rather than flashing every page and then removing most of them.
 */
const CapabilitiesContext = createContext<Capabilities | null>(null);

export function useCapabilities(): Capabilities | null {
  return useContext(CapabilitiesContext);
}

/**
 * Whether a page may be shown.
 *
 * A page with no requirement is always available — `Memory` and `Sessions`
 * are served by the framework itself, not by an extension an instance can
 * decline. While capabilities are unknown, a gated page is withheld.
 */
export function useHasCapability(required?: Capability): boolean {
  const caps = useContext(CapabilitiesContext);
  if (!required) return true;
  if (caps === null) return false;
  return caps[required] === true;
}

export function CapabilitiesProvider({ children }: { children: ReactNode }) {
  const [caps, setCaps] = useState<Capabilities | null>(null);

  useEffect(() => {
    let live = true;
    fetchCapabilities()
      .then((r) => {
        if (live) setCaps(r.capabilities);
      })
      .catch(() => {
        // Unauthenticated, or the endpoint is missing on an older build.
        // An empty map rather than `null`: the answer is "nothing gated is
        // available", which hides those pages instead of withholding the
        // whole sidebar forever.
        if (live) setCaps({});
      });
    return () => {
      live = false;
    };
  }, []);

  return (
    <CapabilitiesContext.Provider value={caps}>
      {children}
    </CapabilitiesContext.Provider>
  );
}
